import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import AttendanceCell from "./AttendanceCell";
import AttendanceClockSyncButton from "./AttendanceClockSyncButton";
import AttendanceDayExceptions from "./AttendanceDayExceptions";
import AttendanceDayOverrideModal from "./AttendanceDayOverrideModal";
import AttendanceLeaveViewModal from "./AttendanceLeaveViewModal";
import AttendanceLegend from "./AttendanceLegend";
import {
	buildSortedAttendanceRows,
	groupAttendanceRows,
} from "./attendanceHelpers";
import {
	buildMonthlyAttendanceMock,
	shouldUseMonthlyAttendanceMock,
} from "./monthlyAttendanceMock";
import {
	Button,
	Card,
	DatePicker,
	Form,
	Modal,
	Select,
	Space,
	TimePicker,
	Typography,
	message,
} from "antd";
import { LeftOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { antdKoKRSunday as locale } from "../../utilities/weekLocale";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

dayjs.locale("ko");

const { Text } = Typography;

const NAME_COL_WIDTH = 300;
const LATE_TOTAL_COL_WIDTH = 76;

const formatLateMinutes = (minutes) => {
	const total = Math.max(0, Math.floor(Number(minutes) || 0));
	if (total < 60) {
		return `${total}분`;
	}
	const hours = Math.floor(total / 60);
	const remain = total % 60;
	if (remain === 0) {
		return `${hours}시간`;
	}
	return `${hours}시간 ${remain}분`;
};

const resolveAttendanceCell = (row, item) => {
	const cell = row.cells?.[item.date];
	if (item.weekday === 6 && (!cell || cell.status === "empty")) {
		return {
			...(cell || {}),
			status: "holiday",
			holiday_kind: cell?.holiday_kind || "주일",
			has_excuse: Boolean(cell?.has_excuse),
			tooltip: cell?.tooltip || "",
		};
	}
	return cell;
};

const MonthlyAttendance = ({ readOnly = false }) => {
	const isMobile = useIsMobile();
	const [month, setMonth] = useState(dayjs());
	const [data, setData] = useState(null);
	const [workTypes, setWorkTypes] = useState([]);
	const [terminals, setTerminals] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState(null);
	const [overrideTarget, setOverrideTarget] = useState(null);
	const [clockTarget, setClockTarget] = useState(null);
	const [clockSaving, setClockSaving] = useState(false);
	const [leaveView, setLeaveView] = useState(null);
	const [hoverCell, setHoverCell] = useState(null);
	const [form] = Form.useForm();
	const [clockForm] = Form.useForm();

	const openLeaveView = (name, date, cell) => {
		const applications = cell?.leave_applications || [];
		if (!applications.length) return;
		setLeaveView({ name, date, applications });
	};

	const openOverride = (employee, date, cell) => {
		setOverrideTarget({
			user_id: employee.user_id,
			name: employee.name,
			date,
			kind: cell?.override_kind || "일반출근",
			override_kind: cell?.override_kind,
		});
	};

	const openClockIn = (employee, date, cell) => {
		const current = cell?.clock_time
			? dayjs(cell.clock_time, ["HH:mm:ss", "HH:mm"])
			: null;
		setClockTarget({
			user_id: employee.user_id,
			name: employee.name,
			date,
			clock_time: cell?.clock_time || null,
		});
		clockForm.setFieldsValue({
			time:
				current && current.isValid()
					? current
					: dayjs("09:00", "HH:mm"),
		});
	};

	const saveClockIn = async () => {
		if (!clockTarget) return;
		try {
			const values = await clockForm.validateFields();
			setClockSaving(true);
			await useAxios.post("/attendance/clock-in/", {
				user_id: clockTarget.user_id,
				date: clockTarget.date,
				time: values.time.format("HH:mm"),
			});
			message.success(
				clockTarget.clock_time
					? "출근 시각을 수정했습니다."
					: "출근 시각을 기록했습니다.",
			);
			setClockTarget(null);
			loadStatus();
		} catch (error) {
			if (error?.errorFields) return;
			message.error(
				error?.response?.data?.error ||
					"출근 시각을 저장하지 못했습니다.",
			);
		} finally {
			setClockSaving(false);
		}
	};

	const loadStatus = useCallback(async () => {
		const year = month.year();
		const monthNum = month.month() + 1;
		const forceMock =
			typeof window !== "undefined" &&
			new URLSearchParams(window.location.search).get(
				"mockAttendance",
			) === "1";

		if (forceMock) {
			setLoading(true);
			setData(buildMonthlyAttendanceMock(year, monthNum));
			message.info(
				"테스트용 근태 현황 데이터를 표시합니다.",
				2,
			);
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			const res = await useAxios.get("/attendance/status/", {
				params: {
					view: "monthly",
					year,
					month: monthNum,
				},
			});
			setData(res.data);
		} catch {
			if (shouldUseMonthlyAttendanceMock()) {
				setData(
					buildMonthlyAttendanceMock(
						year,
						monthNum,
					),
				);
				message.warning(
					"API를 불러오지 못해 테스트용 근태 현황을 표시합니다.",
					3,
				);
			} else {
				message.error(
					"월별 출근 현황을 불러오지 못했습니다.",
				);
			}
		} finally {
			setLoading(false);
		}
	}, [month]);

	const loadWorkTypes = useCallback(async () => {
		if (readOnly) {
			return;
		}
		try {
			const res = await useAxios.get(
				"/attendance/workday-types/",
			);
			setWorkTypes(res.data || []);
			const terminalRes = await useAxios.get(
				"/attendance/terminals/",
			);
			setTerminals(terminalRes.data || []);
		} catch {
			setWorkTypes([]);
		}
	}, [readOnly]);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	useEffect(() => {
		loadWorkTypes();
	}, [loadWorkTypes]);

	const dates = data?.dates || [];
	const todayKey = dayjs().format("YYYY-MM-DD");
	const showsTodayExceptions = dates.some(
		(item) => item.date === todayKey,
	);
	const rows = useMemo(
		() => buildSortedAttendanceRows(data?.employees || []),
		[data?.employees],
	);
	const rowBlocks = useMemo(() => groupAttendanceRows(rows), [rows]);

	const openDateModal = async (dateItem) => {
		setSelectedDate(dateItem);
		const values = {
			work_type: dateItem.work_type_id || undefined,
			start_time: dateItem.start_time
				? dayjs(dateItem.start_time, "HH:mm:ss")
				: undefined,
			allowed_terminal_ids: [],
		};
		if (dateItem.work_day_id) {
			try {
				const res = await useAxios.get(
					"/attendance/work-days/",
					{ params: { date: dateItem.date } },
				);
				const row = (res.data || []).find(
					(item) =>
						item.id ===
						dateItem.work_day_id,
				);
				if (row) {
					values.start_time = row.start_time
						? dayjs(
								row.start_time,
								"HH:mm:ss",
							)
						: values.start_time;
					values.allowed_terminal_ids =
						row.allowed_terminal_ids || [];
				}
			} catch {
				values.allowed_terminal_ids = [];
			}
		}
		form.setFieldsValue(values);
		setModalOpen(true);
	};

	const selectedWorkTypeId = Form.useWatch("work_type", form);
	const selectedWorkType = workTypes.find(
		(type) => type.id === selectedWorkTypeId,
	);
	const isOtherWorkType = selectedWorkType?.code === "other";

	const handleSaveDate = async () => {
		try {
			const values = await form.validateFields();
			const payload = { work_type: values.work_type };
			const chosen = workTypes.find(
				(type) => type.id === values.work_type,
			);
			if (chosen?.code === "other") {
				payload.start_time =
					values.start_time.format("HH:mm:ss");
				payload.allowed_terminal_ids =
					values.allowed_terminal_ids || [];
			}
			if (selectedDate?.work_day_id) {
				await useAxios.put(
					`/attendance/work-days/${selectedDate.work_day_id}/`,
					payload,
				);
			} else {
				await useAxios.post("/attendance/work-days/", {
					date: selectedDate.date,
					...payload,
				});
			}
			message.success("근무 유형이 저장되었습니다.");
			setModalOpen(false);
			loadStatus();
		} catch (err) {
			if (err?.errorFields) return;
			message.error("근무 유형 저장에 실패했습니다.");
		}
	};

	const handleAddDate = async () => {
		if (!selectedDate) return;
		try {
			const values = await form.validateFields();
			const payload = {
				date: selectedDate.date,
				work_type: values.work_type,
			};
			const chosen = workTypes.find(
				(type) => type.id === values.work_type,
			);
			if (chosen?.code === "other") {
				payload.start_time =
					values.start_time.format("HH:mm:ss");
				payload.allowed_terminal_ids =
					values.allowed_terminal_ids || [];
			}
			await useAxios.post("/attendance/work-days/", payload);
			message.success("출근 날짜가 추가되었습니다.");
			setModalOpen(false);
			loadStatus();
		} catch (err) {
			if (err?.errorFields) return;
			message.error("출근 날짜 추가에 실패했습니다.");
		}
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="월별 출근 현황" />
			<div className="page">
				<PageHeader
					title="월별 출근 현황"
				/>
				<Card className="surface-card" loading={loading}>
					<div className="attendance-toolbar">
						<Space>
							<Button
								icon={<LeftOutlined />}
								onClick={() =>
									setMonth(
										(
											prev,
										) =>
											prev.subtract(
												1,
												"month",
											),
									)
								}
							/>
							<DatePicker
								inputReadOnly={isMobile}
								picker="month"
								value={month}
								onChange={(value) =>
									value &&
									setMonth(value)
								}
								allowClear={false}
							/>
							<Button
								icon={<RightOutlined />}
								onClick={() =>
									setMonth(
										(
											prev,
										) =>
											prev.add(
												1,
												"month",
											),
									)
								}
							/>

							<AttendanceClockSyncButton />
						</Space>
						<Space wrap>
							<AttendanceLegend />
						</Space>
				</div>

				{showsTodayExceptions ? (
					<AttendanceDayExceptions
						employees={
							data?.employees || []
						}
						date={todayKey}
						onCellClick={(
							employee,
							cell,
						) => {
							if (
								cell
									?.leave_applications
									?.length
							) {
								openLeaveView(
									employee.name,
									todayKey,
									cell,
								);
								return;
							}
							if (readOnly) return;
							openOverride(
								employee,
								todayKey,
								cell,
							);
						}}
					/>
				) : null}

				<div className="attendance-grid-wrap">
					<div
						className="attendance-grid"
						style={{
							gridTemplateColumns: `72px ${NAME_COL_WIDTH}px repeat(${Math.max(dates.length, 1)}, 36px) ${LATE_TOTAL_COL_WIDTH}px`,
						}}
						onMouseLeave={() =>
							setHoverCell(null)
						}>
						<div className="attendance-grid__group attendance-grid__group--header">
							분류
						</div>
						<div className="attendance-grid__name attendance-grid__name--header">
							이름
						</div>
						{dates.map((item) => {
							const headerTitle = `${item.date} (${item.work_type_name || "미지정"})${
								item.holiday_name
									? ` · ${item.holiday_name}`
									: ""
							}`;
							const colHover =
								hoverCell?.date ===
								item.date
									? " is-col-hover"
									: "";
							const headerClass = `attendance-grid__col-header${
								item.holiday_name
									? " attendance-grid__col-header--holiday"
									: ""
							}${colHover}`;
							const headerBody = (
								<>
									<span className="attendance-grid__weekday">
										{
											item.weekday_label
										}
									</span>
									<span className="attendance-grid__day">
										{dayjs(
											item.date,
										).date()}
									</span>
									{item.holiday_name ? (
										<span
											className="attendance-grid__holiday-name"
											title={
												item.holiday_name
											}>
											{
												item.holiday_name
											}
										</span>
									) : null}
								</>
							);
							if (readOnly) {
								return (
									<div
										key={
											item.date
										}
										className={
											headerClass
										}
										title={
											headerTitle
										}>
										{
											headerBody
										}
									</div>
								);
							}
							return (
								<button
									type="button"
									key={
										item.date
									}
									className={
										headerClass
									}
									onClick={() =>
										openDateModal(
											item,
										)
									}
									title={
										headerTitle
									}>
									{
										headerBody
									}
								</button>
							);
						})}
						<div
							className="attendance-grid__late-total attendance-grid__late-total--header"
							title="지각 시간 누계">
							지각 누계시간
						</div>
						{rowBlocks.map((block) =>
							block.type ===
							"separator" ? (
								<div
									className="attendance-grid__separator"
									key={
										block.key
									}
								/>
							) : (
								<div
									className="attendance-grid__group-block"
									key={
										block.key
									}>
									<div
										className="attendance-grid__group"
										style={{
											gridRow: `span ${block.employees.length}`,
										}}>
										{
											block.group
										}
									</div>
									{block.employees.map(
										(
											row,
										) => (
											<div
												className={`attendance-grid__person${
													hoverCell?.userId ===
													row.user_id
														? " is-row-hover"
														: ""
												}`}
												key={
													row.user_id
												}
												style={{
													gridTemplateColumns: `${NAME_COL_WIDTH}px repeat(${Math.max(dates.length, 1)}, 36px) ${LATE_TOTAL_COL_WIDTH}px`,
												}}
												onMouseEnter={() =>
													setHoverCell(
														(
															prev,
														) => ({
															userId: row.user_id,
															date:
																prev?.date ||
																null,
														}),
													)
												}>
												<div
													className="attendance-grid__name"
													title={
														row.name
													}
													onMouseEnter={() =>
														setHoverCell(
															{
																userId: row.user_id,
																date: null,
															},
														)
													}>
													<span className="attendance-grid__name-text">
														{
															row.name
														}
													</span>
													<span className="attendance-grid__name-stats">
														<span>
															<span
																style={{
																	fontWeight: "bold",
																	marginRight:
																		"3px",
																}}>
																출근:
															</span>
															{row
																.summary
																?.present ||
																0}
														</span>
														<span>
															<span
																style={{
																	fontWeight: "bold",
																	marginRight:
																		"3px",
																}}>
																지각:
															</span>
															{row
																.summary
																?.late ||
																0}
														</span>
														<span>
															<span
																style={{
																	fontWeight: "bold",
																	marginRight:
																		"3px",
																}}>
																결석:
															</span>
															{row
																.summary
																?.absent ||
																0}
														</span>
														<span>
															<span
																style={{
																	fontWeight: "bold",
																	marginRight:
																		"3px",
																}}>
																연차:
															</span>
															{row
																.summary
																?.annual_leave ||
																0}
														</span>
													</span>
												</div>
												{dates.map(
													(
														item,
													) => {
														const cell =
															resolveAttendanceCell(
																row,
																item,
															);
														const clockAction = cell?.clock_time
															? "수정"
															: "입력";
														const displayCell =
															!readOnly &&
															cell?.can_record_clock
																? {
																		...cell,
																		tooltip: cell.tooltip
																			? `${cell.tooltip} · 클릭하여 출근 시각 ${clockAction}`
																			: `클릭하여 출근 시각 ${clockAction}`,
																	}
																: cell;
														return (
															<div
																className={`attendance-grid__cell${
																	hoverCell?.date ===
																	item.date
																		? " is-col-hover"
																		: ""
																}`}
																key={`${row.user_id}-${item.date}`}
																onMouseEnter={() =>
																	setHoverCell(
																		{
																			userId: row.user_id,
																			date: item.date,
																		},
																	)
																}>
																<AttendanceCell
																	cell={
																		displayCell
																	}
																	onClick={
																		displayCell
																			?.leave_applications
																			?.length ||
																		(!readOnly &&
																			(displayCell?.can_override ||
																				displayCell?.can_record_clock))
																			? () => {
																					if (
																						!readOnly &&
																						displayCell?.can_record_clock &&
																						displayCell?.holiday_kind ===
																							"정기휴일"
																					) {
																						openClockIn(
																							row,
																							item.date,
																							displayCell,
																						);
																						return;
																					}
																					if (
																						displayCell
																							?.leave_applications
																							?.length
																					) {
																						openLeaveView(
																							row.name,
																							item.date,
																							displayCell,
																						);
																						return;
																					}
																					if (
																						displayCell?.can_record_clock
																					) {
																						openClockIn(
																							row,
																							item.date,
																							displayCell,
																						);
																						return;
																					}
																					openOverride(
																						row,
																						item.date,
																						displayCell,
																					);
																				}
																			: undefined
																	}
																/>
															</div>
														);
													},
												)}
												<div
													className="attendance-grid__late-total"
													title={formatLateMinutes(
														row
															.summary
															?.late_minutes,
													)}>
													{formatLateMinutes(
														row
															.summary
															?.late_minutes,
													)}
												</div>
											</div>
										),
									)}
								</div>
							),
						)}
						{!loading &&
						rows.length === 0 ? (
							<div className="attendance-grid__empty">
								등록된 교직원
								정보가 없습니다.
							</div>
						) : null}
					</div>
				</div>
			</Card>

			{!readOnly && (
				<>
					<Modal
						title={
							selectedDate
								? `${selectedDate.date} 근무 유형`
								: "근무 유형"
						}
						open={modalOpen}
						onCancel={() =>
							setModalOpen(false)
						}
						footer={[
							<Button
								key="cancel"
								onClick={() =>
									setModalOpen(
										false,
									)
								}>
								닫기
							</Button>,
							!selectedDate?.has_work_day ? (
								<Button
									key="add"
									icon={
										<PlusOutlined />
									}
									onClick={
										handleAddDate
									}>
									출근
									날짜
									추가
								</Button>
							) : null,
							<Button
								key="save"
								type="primary"
								onClick={
									handleSaveDate
								}>
								저장
							</Button>,
						]}
						destroyOnClose>
						{selectedDate ? (
							<Space
								direction="vertical"
								size="small"
								style={{
									width: "100%",
								}}>
								<Text type="secondary">
									현재:{" "}
									{selectedDate.work_type_name ||
										"미지정"}
									{selectedDate.has_work_day
										? " (저장됨)"
										: " (요일 기본)"}
								</Text>
								<Form
									form={
										form
									}
									layout="vertical">
									<Form.Item
										name="work_type"
										label="근무 유형"
										rules={[
											{
												required: true,
												message: "근무 유형을 선택하세요",
											},
										]}>
										<Select
											options={workTypes.map(
												(
													type,
												) => ({
													value: type.id,
													label:
														type.code ===
														"other"
															? type.name
															: `${type.name} (${String(type.start_time).slice(0, 5)})`,
												}),
											)}
											placeholder="유형 선택"
										/>
									</Form.Item>
									{isOtherWorkType ? (
										<>
											<Form.Item
												name="start_time"
												label="출근 시각"
												rules={[
													{
														required: true,
														message: "출근 시각을 선택하세요",
													},
												]}>
												<TimePicker
													inputReadOnly={isMobile}
													format="HH:mm"
													style={{
														width: "100%",
													}}
												/>
											</Form.Item>
											<Form.Item
												name="allowed_terminal_ids"
												label="허용 단말기"
												rules={[
													{
														required: true,
														type: "array",
														min: 1,
														message: "단말기를 하나 이상 선택하세요",
													},
												]}>
												<Select
													mode="multiple"
													placeholder="단말기 선택"
													options={terminals.map(
														(
															terminal,
														) => ({
															value: terminal.id,
															label: `${terminal.name} (${terminal.terminal_id})`,
														}),
													)}
												/>
											</Form.Item>
										</>
									) : null}
								</Form>
							</Space>
						) : null}
					</Modal>
					<AttendanceDayOverrideModal
						open={Boolean(overrideTarget)}
						target={overrideTarget}
						onClose={() =>
							setOverrideTarget(null)
						}
						onSaved={loadStatus}
					/>
					<Modal
						title={
							clockTarget
								? `${clockTarget.name} — ${clockTarget.date} 출근 시각`
								: "출근 시각"
						}
						open={Boolean(clockTarget)}
						onCancel={() =>
							setClockTarget(null)
						}
						onOk={saveClockIn}
						confirmLoading={clockSaving}
						okText="저장"
						destroyOnClose>
						<Form
							form={clockForm}
							layout="vertical">
							<Form.Item
								name="time"
								label="출근 시각"
								rules={[
									{
										required: true,
										message: "출근 시각을 입력해주세요.",
									},
								]}>
								<TimePicker
									inputReadOnly={isMobile}
									format="HH:mm"
									style={{ width: "100%" }}
								/>
							</Form.Item>
							<Text type="secondary">
								입력한 시각으로 출근·지각이
								다시 계산됩니다. 04:00
								이전은 출근으로 인정되지
								않습니다.
							</Text>
						</Form>
					</Modal>
				</>
			)}
			<AttendanceLeaveViewModal
				open={Boolean(leaveView)}
				target={leaveView}
				onClose={() => setLeaveView(null)}
			/>
			</div>
		</ConfigProvider>
	);
};

export default MonthlyAttendance;
